import { useCallback, useEffect, useState } from 'react';
import { Button, Card } from '@heroui/react';

import ActivityItem from '../../common/ActivityItem';
import Notice from '../../common/Notice';
import Pagination from '../../common/Pagination';
import ViewTab from '../../common/ViewTab';
import { SkeletonCard } from '../../common/Skeleton';
import { countUnread, listNotifications, markAllRead, markRead } from '../../../services/activity.service';
import { ACTIVITY, messageOf } from '../../../i18n/el';

// Ο κατάλογος ειδοποιήσεων (ΛΑ-22).
//
// **Διασυλλογικός, γιατί η ΛΑ-22 μιλά για το πρόσωπο** και όχι για τη συλλογή:
// «οι χρήστες πρέπει να ενημερώνονται για ενέργειες που τους αφορούν». Το εύρος
// είναι ο χρήστης, και ο έλεγχος ορατότητας γίνεται στον εξυπηρετητή — εδώ δεν
// υπάρχει και δεν επιτρέπεται να υπάρχει δεύτερη διατύπωσή του.

export default function NotificationsPage({ onUnreadChange = null }) {
    const [entries, setEntries] = useState([]);
    const [meta, setMeta] = useState(null);
    const [page, setPage] = useState(1);
    // ⚠ **ΠΡΟΕΠΙΛΟΓΗ ΟΙ ΑΔΙΑΒΑΣΤΕΣ (ΚΔ4).** Ό,τι διαβάζει ο χρήστης πρέπει να
    // φεύγει από μπροστά του: με προεπιλογή «Όλες», η σήμανση μιας ειδοποίησης
    // δεν άλλαζε **τίποτα** στην οθόνη — η γραμμή έμενε στη θέση της, χωρίς την
    // ένδειξη, και η ενέργεια έμοιαζε να μην έκανε τίποτα.
    //
    // **Δεν γράφεται στη διεύθυνση**, και είναι σκόπιμο: η διεύθυνση κρατά ό,τι
    // ο χρήστης **επέλεξε**, όχι ό,τι βρήκε. Προεπιλογή γραμμένη εκεί κάνει κάθε
    // κοινοποιημένο σύνδεσμο να κουβαλάει μια απόφαση που δεν πήρε κανείς.
    const [onlyUnread, setOnlyUnread] = useState(true);
    // Η μαζική σήμανση: η επιβεβαίωση κρατά **δικό της** πλήθος, ζητημένο τη
    // στιγμή που ανοίγει.
    const [confirming, setConfirming] = useState(null);
    const [bulkError, setBulkError] = useState('');
    const [isBulkBusy, setBulkBusy] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Σφάλμα **ανά γραμμή**: μια αποτυχία σήμανσης αφορά μία ειδοποίηση και
    // εμφανίζεται πάνω της, όχι σε κοινό μήνυμα στην κορυφή μιας λίστας που
    // κυλάει.
    const [rowError, setRowError] = useState({});
    const [marking, setMarking] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const result = await listNotifications({ page, unread: onlyUnread ? 'true' : undefined });
            setEntries(result.data);
            setMeta(result.meta);
        } catch (caught) {
            setError(messageOf(caught));
        } finally {
            setLoading(false);
        }
    }, [page, onlyUnread]);

    useEffect(() => {
        load();
    }, [load]);

    async function handleMarkRead(entry) {
        setMarking(entry.id);
        setRowError((previous) => ({ ...previous, [entry.id]: '' }));
        try {
            await markRead(entry.id);
            await load();
            // Το κέλυφος κρατά το πλήθος της σήμανσης: χωρίς αυτό, ο αριθμός στο
            // μενού θα έμενε παγωμένος ώσπου να αλλάξει σελίδα ο χρήστης.
            onUnreadChange?.();
        } catch (caught) {
            setRowError((previous) => ({ ...previous, [entry.id]: messageOf(caught) }));
        } finally {
            setMarking(null);
        }
    }

    /**
     * Το πλήθος **ζητιέται τη στιγμή που ανοίγει η επιβεβαίωση**, από τη διαδρομή
     * που μετρά — όχι από το `meta` της σελίδας.
     *
     * Είναι το επιχείρημα της **D25**, στην ίδια μορφή: το `meta.total` είναι όσο
     * παλιό είναι η φόρτωση, και **ψευδής αριθμός σε μη αναστρέψιμη ενέργεια
     * είναι χειρότερος από κανέναν, γιατί ο χρήστης τον εμπιστεύεται**. Εδώ
     * υπάρχει και δεύτερος λόγος: στην καρτέλα «Όλες» το `meta.total` δεν είναι
     * καν το ζητούμενο μέγεθος — μετράει διαβασμένες και αδιάβαστες μαζί.
     */
    async function openConfirm() {
        setBulkError('');
        setBulkBusy(true);
        setConfirming({ count: null });
        try {
            setConfirming({ count: await countUnread() });
        } catch (caught) {
            setConfirming(null);
            setBulkError(messageOf(caught));
        } finally {
            setBulkBusy(false);
        }
    }

    function closeConfirm() {
        setConfirming(null);
        setBulkError('');
    }

    async function confirmMarkAll() {
        setBulkBusy(true);
        setBulkError('');
        try {
            await markAllRead();
            setConfirming(null);
            await load();
            onUnreadChange?.();
        } catch (caught) {
            setBulkError(messageOf(caught));
        } finally {
            setBulkBusy(false);
        }
    }

    function changeScope(unread) {
        setOnlyUnread(unread);
        // Η αλλαγή εύρους μηδενίζει τη σελίδα: η σελίδα 3 των αδιάβαστων συχνά
        // δεν υπάρχει, και ο χρήστης θα έβλεπε κενό κατάλογο για γεμάτο εύρος.
        setPage(1);
    }

    // Ονομασμένες, όχι ενσωματωμένα βέλη: ο στατικός έλεγχος καλωδίωσης διαβάζει
    // τα χαρακτηριστικά JSX ως κείμενο μέχρι το πρώτο `>`, και το βέλος έχει ένα.
    function showUnread() {
        changeScope(true);
    }

    function showAll() {
        changeScope(false);
    }

    return (
        <Card>
            <Card.Header>
                <Card.Title>{ACTIVITY.notificationsTitle}</Card.Title>
                <Card.Description>{ACTIVITY.notificationsIntro}</Card.Description>
            </Card.Header>

            <Card.Content className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-2" role="tablist" aria-label={ACTIVITY.notificationsTitle}>
                        <ViewTab icon="fa-solid fa-circle-dot" isActive={onlyUnread} onPress={showUnread}>
                            {ACTIVITY.onlyUnread}
                        </ViewTab>
                        <ViewTab icon="fa-solid fa-list" isActive={!onlyUnread} onPress={showAll}>
                            {ACTIVITY.all}
                        </ViewTab>
                    </div>

                    {/* Η μαζική ενέργεια **έξω** από τις καρτέλες: αφορά τις
                        ειδοποιήσεις ως σύνολο και όχι το εύρος που βλέπεις. */}
                    <Button variant="outline" size="sm" onPress={openConfirm} isDisabled={isBulkBusy}>
                        <i className="fa-solid fa-check" aria-hidden="true" />
                        {ACTIVITY.markAll}
                    </Button>
                </div>

                {/* ΜΛΑ-03: **επιβεβαίωση πριν από μαζική και μη αναστρέψιμη
                    ενέργεια**, με το πλήθος μέσα στο κείμενο. Μένει στη σελίδα
                    αντί να ανοίξει διάλογο, όπως η επιβεβαίωση διαγραφής
                    καθηκόντος: δεύτερο επίπεδο δεν προσθέτει τίποτα εδώ και
                    φέρνει μαζί του το πρόβλημα του κλεισίματος (D26). */}
                {confirming && (
                    <div className="flex flex-col gap-3 rounded-lg border border-danger p-3">
                        <Notice status="warning" title={ACTIVITY.markAllTitle}>
                            {confirming.count === null
                                ? ACTIVITY.markAllCounting
                                : confirming.count === 0
                                    ? ACTIVITY.markAllNone
                                    : ACTIVITY.markAllWarning(confirming.count)}
                        </Notice>

                        {bulkError && (
                            <Notice status="danger" title={ACTIVITY.markAllFailed}>{bulkError}</Notice>
                        )}

                        <div className="flex flex-wrap gap-2">
                            {/* Το κουμπί επιβεβαίωσης **δεν υπάρχει** όταν δεν
                                υπάρχει τίποτα να σημειωθεί: ενεργό κουμπί που δεν
                                κάνει τίποτα είναι χειρότερο από απόν. */}
                            {confirming.count > 0 && (
                                <Button color="danger" onPress={confirmMarkAll} isPending={isBulkBusy}>
                                    {ACTIVITY.markAllConfirm}
                                </Button>
                            )}
                            <Button variant="outline" onPress={closeConfirm}>
                                {ACTIVITY.markAllCancel}
                            </Button>
                        </div>
                    </div>
                )}

                {bulkError && !confirming && (
                    <Notice status="danger" title={ACTIVITY.markAllFailed}>{bulkError}</Notice>
                )}

                {error && <Notice status="danger" title={ACTIVITY.notificationsError}>{error}</Notice>}

                {isLoading && <SkeletonCard />}

                {!isLoading && !error && entries.length === 0 && (
                    <p className="py-12 text-center text-muted">
                        {onlyUnread ? ACTIVITY.unreadEmpty : ACTIVITY.notificationsEmpty}
                    </p>
                )}

                {!isLoading && entries.length > 0 && (
                    <ul className="flex flex-col gap-2">
                        {entries.map((entry) => (
                            <ActivityItem
                                key={entry.id}
                                entry={entry}
                                onMarkRead={handleMarkRead}
                                isMarking={marking === entry.id}
                                error={rowError[entry.id] ?? ''}
                            />
                        ))}
                    </ul>
                )}

                <Pagination meta={meta} onPage={setPage} noun="ειδοποιήσεις" />
            </Card.Content>
        </Card>
    );
}
